import React, { useState, useEffect } from 'react';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiRefreshCw, FiCheck, FiStar, FiAward, FiSmile } = FiIcons;

const MatchingGame = () => {
  const [cards, setCards] = useState([]);
  const [turns, setTurns] = useState(0);
  const [choiceOne, setChoiceOne] = useState(null);
  const [choiceTwo, setChoiceTwo] = useState(null);
  const [matches, setMatches] = useState(0);
  const [isWon, setIsWon] = useState(false);

  // Vocabulary Data
  const vocabItems = [
    { am: 'እኔ', en: 'I', type: 'pronoun' },
    { am: 'እኛ', en: 'We', type: 'pronoun' },
    { am: 'ቤት', en: 'House', type: 'noun' },
    { am: 'ሰላም', en: 'Hello', type: 'greeting' },
    { am: 'አባት', en: 'Father', type: 'noun' },
    { am: 'እናት', en: 'Mother', type: 'noun' }
  ];

  // Shuffle cards
  const shuffleCards = () => {
    // Create pairs: one Amharic, one English, linked by a matching ID
    const shuffledCards = [...vocabItems, ...vocabItems]
      .map((item, index) => {
        // First half of array gets Amharic, second half gets English
        const isAmharic = index < vocabItems.length;
        return {
          id: Math.random(),
          matchId: item.en, // Match based on English key
          text: isAmharic ? item.am : item.en,
          lang: isAmharic ? 'am' : 'en',
          matched: false
        };
      })
      .sort(() => Math.random() - 0.5);

    setChoiceOne(null);
    setChoiceTwo(null);
    setCards(shuffledCards);
    setTurns(0);
    setMatches(0);
    setIsWon(false);
  };

  useEffect(() => {
    shuffleCards();
  }, []);

  // Handle Choice
  const handleChoice = (card) => {
    if (!choiceOne) {
      setChoiceOne(card);
    } else if (!choiceTwo && card.id !== choiceOne.id) {
      setChoiceTwo(card);
    }
  };

  // Compare 2 selected cards
  useEffect(() => {
    if (choiceOne && choiceTwo) {
      if (choiceOne.matchId === choiceTwo.matchId) {
        setCards(prevCards => {
          return prevCards.map(card => {
            if (card.matchId === choiceOne.matchId) {
              return { ...card, matched: true };
            }
            return card;
          });
        });
        setMatches(m => m + 1);
        resetTurn();
      } else {
        setTimeout(() => resetTurn(), 1000);
      }
    }
  }, [choiceOne, choiceTwo]);

  useEffect(() => {
    if (matches === vocabItems.length && matches > 0) {
      setIsWon(true);
    }
  }, [matches]);

  const resetTurn = () => {
    setChoiceOne(null);
    setChoiceTwo(null);
    setTurns(prevTurns => prevTurns + 1);
  };

  return (
    <PageLayout
      title="Word Matching Game"
      subtitle="Match the Amharic words with their English meanings. A great way to test your memory and vocabulary!"
      icon={FiGrid}
      color="text-green-600"
      bgColor="bg-green-50"
      prevLink="/teacher-hub"
      prevLabel="Teacher Hub"
    >
      <div className="max-w-4xl mx-auto fade-in">
        {/* Stats Bar */}
        <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="flex gap-6">
            <div className="text-center">
              <span className="text-xs text-gray-500 uppercase font-bold">Turns</span>
              <div className="text-2xl font-bold text-gray-800">{turns}</div>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500 uppercase font-bold">Matches</span>
              <div className="text-2xl font-bold text-green-600">{matches} / {vocabItems.length}</div>
            </div>
          </div>
          <button 
            onClick={shuffleCards}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} /> Restart
          </button>
        </div>

        {/* Win State */}
        {isWon && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-8 text-white text-center mb-8 shadow-xl animate-bounce-in">
            <SafeIcon icon={FiAward} className="text-6xl text-white mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">Wonderful Job!</h2>
            <p className="text-xl opacity-90 mb-6">You matched all the words in {turns} turns.</p>
            <button 
              onClick={shuffleCards}
              className="bg-white text-orange-600 px-8 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
            >
              Play Again
            </button>
          </div>
        )}

        {/* Game Grid */}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
          {cards.map(card => (
            <div 
              key={card.id} 
              className={`relative h-32 cursor-pointer perspective-1000`}
              onClick={() => !choiceTwo && !card.matched && handleChoice(card)}
            >
              <div className={`w-full h-full transition-all duration-500 transform style-preserve-3d ${
                card === choiceOne || card === choiceTwo || card.matched ? 'rotate-y-180' : ''
              }`}>
                {/* Front (Hidden) */}
                <div className="absolute inset-0 backface-hidden bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md flex items-center justify-center border-2 border-white/20">
                  <SafeIcon icon={FiStar} className="text-3xl text-white/30" />
                </div>

                {/* Back (Revealed) */}
                <div className={`absolute inset-0 backface-hidden rotate-y-180 rounded-xl shadow-md flex items-center justify-center border-4 ${
                  card.matched 
                    ? 'bg-green-50 border-green-400' 
                    : 'bg-white border-indigo-200'
                }`}>
                  <div className="text-center">
                    <span className={`block text-xl font-bold ${
                      card.lang === 'am' ? 'font-amharic text-2xl mb-1' : 'font-sans'
                    } ${
                      card.matched ? 'text-green-700' : 'text-gray-800'
                    }`}>
                      {card.text}
                    </span>
                    {card.lang === 'am' && <span className="text-[10px] text-gray-400 uppercase tracking-widest">Amharic</span>}
                    {card.lang === 'en' && <span className="text-[10px] text-gray-400 uppercase tracking-widest">English</span>}
                  </div>
                  {card.matched && (
                    <div className="absolute top-2 right-2 text-green-500">
                      <SafeIcon icon={FiCheck} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};

export default MatchingGame;